import { type MutationHookOptions, useMutation } from '@apollo/react-hooks';
import { type ApolloClient } from 'apollo-client';
import gql from 'graphql-tag';
import { useRef } from 'react';
import { isFedRamp, isIsolatedCloud, isolatedCloudDomain } from '@atlassian/atl-context';
import {
	type ContextId,
	type ExtensionId,
	type ProductEnvironment,
	type ForgeCDNEnvironment,
} from '@atlassian/forge-ui-types';
import {
	type GQLUserAuthTokenForExtensionInput,
	type GQLUserAuthTokenForExtensionResponse,
} from '../../../web-client/graphql/types';
import { useTracingContext } from '../../../error-reporting';
import {
	base64ToBlob,
	blobToBase64,
	getCDNEnvironment,
	createAtlAttributionHeader,
} from '../../../utils';
import { fg } from '@atlaskit/platform-feature-flags';
import { createAppAri } from '@atlassian/cs-ari';
import { getAppPerfTrackingEnabled, getPerformanceMark } from '../../../performance-profiling';
import { usePerformanceAnalytics } from '../../../performance-profiling/usePerformanceAnalytics';
import type { DocumentNode } from 'graphql';
import { MILLIS_IN_SECOND, useFetchAuthToken } from './useFetchAuthToken';

export const authTokenForExtensionMutation: DocumentNode = gql`
	mutation forge_ui_userAuthTokenForExtension($input: UserAuthTokenForExtensionInput!) {
		userAuthTokenForExtension(input: $input) {
			success
			errors {
				message
			}
			authToken {
				token
				ttl
			}
		}
	}
`;

export const tcsByCloudIdQuery: DocumentNode = gql`
	query tcsByCloudId($cloudIds: [ID!], $product: String!) {
		tenantContexts(cloudIds: $cloudIds) {
			activationIdByProduct(product: $product) {
				targetRegion
			}
		}
	}
`;

export interface MutationVariables {
	input: GQLUserAuthTokenForExtensionInput;
}

export interface MutationData {
	userAuthTokenForExtension: GQLUserAuthTokenForExtensionResponse;
}

interface TcsByCloudIdQueryVariables {
	cloudIds: string[];
	product: Product;
}

interface TcsByCloudIdQueryData {
	tenantContexts: {
		activationIdByProduct: {
			targetRegion: string;
		};
	}[];
}

export interface AuthToken {
	expiry: number; // expiry in ms
	token: string;
}

const validProducts = ['confluence', 'jira', 'bitbucket'] as const;
export type Product = (typeof validProducts)[number];

export type OAuthFetchClient = (
	restPath: string,
	product: Product,
	init: Omit<RequestInit, 'body'> & { body?: string | null },
	isMultipartFormData: boolean,
) => Promise<Response>;

interface useProductFetchClientOptions {
	accountType?: 'atlassian' | 'customer' | 'anonymous';
	appId: string;
	cloudId?: string;
	contextIds: ContextId[];
	environment: ProductEnvironment;
	extensionId: ExtensionId;
	options?: MutationHookOptions<MutationData, MutationVariables>;
	renderThreeLOPrompt: (retry: () => Promise<void>) => Promise<void>;
	workspaceId?: string;
}

const apiGatewayUrlMap: Record<ForgeCDNEnvironment, string> = {
	dev: 'api.stg.atlassian.com',
	stg: 'api.stg.atlassian.com',
	prod: 'api.atlassian.com',
};

const getApiGatewayUrl = (environment: ForgeCDNEnvironment): string => {
	const apiBase = apiGatewayUrlMap[environment];
	const url = isFedRamp() ? apiBase.replace('atlassian.com', 'atlassian-us-gov-mod.com') : apiBase;

	if (!url) {
		throw new Error(`Invalid environment: ${environment}`);
	}

	return url;
};

const getBaseUrlForIsolatedCloud = (product: Product, cloudId?: string) => {
	if (product === 'bitbucket') {
		throw new Error('product not supported in Isolated Cloud yet.');
	}
	const apiBase = `api.${isolatedCloudDomain()}`;
	return `https://${apiBase}/ex/${product}/${cloudId}`;
};

function getBaseUrlForCommercialAndFedRamp(
	environment: ForgeCDNEnvironment,
	product: Product,
	cloudId?: string,
): string {
	if (product === 'bitbucket') {
		return environment === 'prod'
			? 'https://api.bitbucket.org'
			: 'https://api.integration.bb-inf.net';
	}
	const apiBase = getApiGatewayUrl(environment);
	return `https://${apiBase}/ex/${product}/${cloudId}`;
}

function getBaseUrl(environment: ForgeCDNEnvironment, product: Product, cloudId?: string): string {
	if (fg('forge-ui-oasis-enabled')) {
		return isIsolatedCloud()
			? getBaseUrlForIsolatedCloud(product, cloudId)
			: getBaseUrlForCommercialAndFedRamp(environment, product, cloudId);
	}
	return getBaseUrlForCommercialAndFedRamp(environment, product, cloudId);
}

function createProductUrl(
	environment: ForgeCDNEnvironment,
	product: Product,
	restPath: string,
	cloudId?: string,
): string {
	const baseUrl = getBaseUrl(environment, product, cloudId);
	const fullUrl =
		// bitbucket requests could be made with absolute URLs
		// e.g. when following href links in API responses
		product === 'bitbucket' && restPath.startsWith(`${baseUrl}/`)
			? restPath
			: `${baseUrl}${restPath}`;
	const productUrl = new URL(fullUrl).toString();

	if (!productUrl.startsWith(`${baseUrl}/`)) {
		throw new Error('Invalid product URL');
	}

	return productUrl;
}

export function isTokenValid(token: AuthToken | undefined): token is AuthToken {
	return !!(token && token.expiry > Date.now());
}

function isProduct(product: unknown): product is Product {
	return validProducts.includes(product as Product);
}

export function isBinaryContentType(contentType: string | undefined | null): boolean {
	return Boolean(
		contentType && !contentType.includes('application/json') && contentType.search(/text\//) === -1,
	);
}

function parseFormData(formLike: Record<string, string>): FormData {
	const form = new FormData();

	for (const [key, value] of Object.entries(formLike)) {
		if (key === 'file') {
			const fileName = formLike['__fileName'];
			const mimeType = formLike['__fileType'];

			// convert back from base64 to File (Blob)
			const blob = base64ToBlob(value, mimeType);
			const file = new File([blob], fileName, { type: mimeType });

			form.append('file', file);
		} else {
			form.append(key, value);
		}
	}

	// remove request metadata properties
	form.delete('__fileName');
	form.delete('__fileType');

	return form;
}

const requestProduct = async (url: string, init?: RequestInit): Promise<Response> => {
	const response = await fetch(url, init);

	const isAttachment = isBinaryContentType(response.headers.get('content-type'));
	if (response.ok && response.body !== null && response.status !== 204 && isAttachment) {
		const b64Content = await blobToBase64(await response.blob());

		return new Response(b64Content, {
			status: response.status,
			statusText: response.statusText,
			headers: response.headers,
		});
	}

	return response;
};

const getTenantRegion = async (
	product: Product,
	cloudIds: string[],
	client: ApolloClient<object>,
): Promise<string> => {
	const { data } = await client.query<TcsByCloudIdQueryData, TcsByCloudIdQueryVariables>({
		query: tcsByCloudIdQuery,
		variables: {
			cloudIds,
			product,
		},
	});

	return data?.tenantContexts[0].activationIdByProduct.targetRegion || '';
};


export function useProductFetchClient({
	appId,
	extensionId,
	accountType,
	environment,
	cloudId,
	workspaceId,
	renderThreeLOPrompt,
	options,
	contextIds = [],
}: useProductFetchClientOptions): OAuthFetchClient {
	const { captureEvent } = usePerformanceAnalytics();

	const [userAuthTokenForExtensionMutationFunction] = useMutation<MutationData, MutationVariables>(
		authTokenForExtensionMutation,
		options,
	);

	const fetchAuthToken = useFetchAuthToken({ renderThreeLOPrompt });

	const authTokenRef = useRef<AuthToken | undefined>(undefined);
	const tenantRegionRef = useRef<string | undefined>(undefined);

	const tracing = useTracingContext();

	/**
	 * Workspace based products like Trello and Bitbucket will not pass in cloudId,
	 * they will pass in workspaceId instead.
	 */
	if (!cloudId && !workspaceId) {
		return async () => {
			throw new Error(
				'Product APIs are not available in this module. Remove requestConfluence, requestJira or requestBitbucket from your code.',
			);
		};
	}

	const fetchOAuthToken = fg('forge-ui-user-auth-token-relay-mutation') ? fetchAuthToken : async (
		extensionId: ExtensionId,
		contextIds: ContextId[],
	): Promise<AuthToken> => {
		tracing?.recordGqlCall('userAuthTokenForExtension - start');

		const { data } = await userAuthTokenForExtensionMutationFunction({
			variables: {
				input: {
					extensionId,
					contextIds,
				},
			},
			context: {
				headers: {
					'atl-attribution': createAtlAttributionHeader(contextIds.sort().join()),
				},
			},
		});

		if (data?.userAuthTokenForExtension) {
			const { success, authToken, errors } = data.userAuthTokenForExtension;

			tracing?.recordGqlCall('userAuthTokenForExtension - end');

			if (
				!success &&
				errors &&
				errors[0].message.includes('No Atlassian OAuth token found for this user and extension')
			) {
				return new Promise((resolve) => {
					renderThreeLOPrompt(async () => {
						const token = await fetchOAuthToken(extensionId, contextIds);
						resolve(token);
					});
				});
			}

			if (success && authToken) {
				const { token, ttl } = authToken;

				return { token, expiry: Date.now() + ttl * MILLIS_IN_SECOND };
			}
		}

		throw new Error('An unexpected error occurred when fetching an auth token');
	};

	const cdnEnvironment = getCDNEnvironment(environment);

	return async (restPath, product, init, isMultipartFormData) => {
		if (!isProduct(product)) {
			throw new Error(`Invalid product: ${product}`);
		}

		const requestAuthStartMark = getPerformanceMark();
		captureEvent?.({
			type: 'product-api-request-initiated',
			elapsed: requestAuthStartMark,
		});

		const atlassianAccount = fg('forge-ui-allow-anon-rest-calls')
			? // New: don't request auth for anonymous accounts.
				accountType === 'atlassian'
			: // Old: assumed authentication was required for unknown account types.
				!accountType || accountType === 'atlassian';

		if (!isTokenValid(authTokenRef.current) && atlassianAccount) {
			if (fg('forge-ui-enable-region-steering')) {
				const fetchAuthTokenPromise = fetchOAuthToken(extensionId, contextIds);
				let getTenantRegionPromise: Promise<string> | undefined;

				// We need to fetch the tenant region to know which region to steer the request to.
				if (cloudId && options?.client) {
					getTenantRegionPromise = getTenantRegion(product, [cloudId], options.client);
				}

				const [authToken, tenantRegion] = await Promise.all([
					fetchAuthTokenPromise,
					getTenantRegionPromise ? getTenantRegionPromise : Promise.resolve(undefined),
				]);
				authTokenRef.current = authToken;
				tenantRegionRef.current = tenantRegion;
			} else {
				authTokenRef.current = await fetchOAuthToken(extensionId, contextIds);
			}
		}

		const headers = new Headers(init.headers);

		// `Bearer undefined` is valid for anonymous requests.
		headers.set('Authorization', `Bearer ${authTokenRef?.current?.token}`);

		// Set the region hit for the request if it's available
		if (tenantRegionRef.current) {
			headers.set('atl-edge-region-hint', tenantRegionRef.current);
		}

		// this header is used to identify requests made by Forge, based on:
		// https://hello.atlassian.net/wiki/x/O5jUIwE?atlOrigin=eyJpIjoiM2NhNjU1MDUxOWJkNDcxNThiNzJjNWU1NzE5Y2FmZTMiLCJwIjoiYyJ9
		const appAri = createAppAri({ appId }).toString();
		headers.set('x-ecosystem-user-agent', `forge ${appAri}`);

		const body = isMultipartFormData ? parseFormData(JSON.parse(init.body as string)) : init.body;

		if (isMultipartFormData) {
			// let the fetch api correctly set the multipart/form-data content-type
			// https://github.com/github/fetch/issues/505#issuecomment-293064470
			headers.delete('content-type');
		}

		const request = requestProduct(createProductUrl(cdnEnvironment, product, restPath, cloudId), {
			...init,
			body,
			headers,
		});

		if (getAppPerfTrackingEnabled()) {
			const requestAuthEndMark = getPerformanceMark();
			request.then(() => {
				const requestEndMark = getPerformanceMark();
				captureEvent?.({
					type: 'product-api-requested',
					duration: requestEndMark - requestAuthStartMark,
					authDuration: requestAuthEndMark - requestAuthStartMark,
					elapsed: requestEndMark,
				});
			});
		}

		return request;
	};
}
